/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2025 (original work) Open Assessment Technologies SA ;
 */
import { speeds, pitches, voices } from '../preferences.js';
import { findClosestElementWithALang, getLanguagePart } from '../util/locale.js';
import { isTextSelected, getSelectionAncestor } from '../util/selection.js';
import { generateElementId } from '../util/dom.js';
import { createScrollTargetObserver } from '../util/autoscroll.js';

const speedMap = Object.freeze({
    [speeds.slowest]: 3,
    [speeds.slow]: 5,
    [speeds.normal]: 2,
    [speeds.fast]: 1,
    [speeds.fastest]: 4
});

const pitchMap = Object.freeze({
    [pitches.lowest]: 3,
    [pitches.low]: 3,
    [pitches.medium]: 2,
    [pitches.high]: 1,
    [pitches.highest]: 1
});

const voiceMap = Object.freeze({
    [voices.female]: 'F',
    [voices.male]: 'M'
});

//Readweb language map for non standard codes
const langMap = {
    da: 'dk',
    nb: 'no'
};

const ignoreClass = 'vFact_DoNotReadAloud';
const containerId = 'readweb-container';
const highlightClass = 'vFact_highlighted';

let reading = false;

/**
 * Load the Readweb initial script. This has to be done once per page.
 * @param {string} url - the full of the script
 * @param {number} [loadingTimeoutMs] - script loading timeout
 * @returns {Promise} resolves when the script is loaded or if it's already loaded
 */
function loadReadWebScript(url, loadingTimeoutMs = 30000) {
    const currentScript = document.head.querySelector('script[data-tts="readweb"]');
    if (currentScript !== null) {
        if (currentScript.dataset.loaded) {
            return Promise.resolve();
        }
        document.head.removeChild(currentScript);
    }

    let iframeContentInterval;
    let timeout;
    return Promise.race([
        new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.addEventListener('load', () => {
                if ('startleseweb' in window) {
                    //Readweb entry point
                    window.startleseweb();

                    //TODO check if this check is reliable with the provider
                    iframeContentInterval = setInterval(() => {
                        const frame = document.getElementById('vFact_audioFrame');
                        if (frame && 'vFactDetect2' in frame.contentWindow) {
                            clearInterval(iframeContentInterval);

                            //ensure the frame doesn't break the layout
                            frame.style.position = 'absolute';
                            frame.style.display = 'block';

                            script.dataset.loaded = true;
                            resolve();
                        }
                    }, 50);
                } else {
                    reject(new Error(`Unable to find the readweb entrypoint 'startleseweb'`));
                }
            });
            script.addEventListener('error', reject);
            script.type = 'text/javascript';
            script.async = true;
            script.src = url;
            script.dataset.tts = 'readweb';
            document.head.appendChild(script);
        }),
        new Promise((resolve, reject) => {
            timeout = setTimeout(
                () => reject(new Error('Timeout: unable to load the read web script')),
                loadingTimeoutMs
            );
        })
    ]).finally(() => {
        if (timeout) {
            clearTimeout(timeout);
        }
        if (iframeContentInterval) {
            clearInterval(iframeContentInterval);
        }
    });
}

/**
 * Readweb toolbar is added as a sibling of a configured id,
 * so to hide it we create an hidden element
 */
function createToolBarContainer() {
    if (!document.getElementById(containerId)) {
        const topContainer = document.createElement('div');
        topContainer.style.position = 'absolute';
        topContainer.style.left = '-10000px';
        topContainer.style.top = 'auto';
        topContainer.style.width = '1px';
        topContainer.style.height = '1px';
        topContainer.style.overflow = 'hidden';
        const container = document.createElement('div');
        container.id = containerId;
        topContainer.appendChild(container);

        document.body.appendChild(topContainer);
    }
}

/**
 * Readweb supports only the language subtag of a locale code,
 * and with some non standard values. So we swap the value of the lang attribute
 * with the value for Readweb during the reading
 * @param {HTMLElement} element
 */
function swapLangAttributes(element) {
    const langHolderElement = findClosestElementWithALang(element);
    const children = langHolderElement ? langHolderElement.querySelectorAll('[lang]') : [];
    for (const elt of [langHolderElement, ...children]) {
        if (elt) {
            const langValue = elt.getAttribute('lang');
            const langSubPart = getLanguagePart(langValue);
            if (langValue && langSubPart) {
                elt.dataset.ttsOriginalLang = langValue;
                elt.setAttribute('lang', langMap[langSubPart] ? langMap[langSubPart] : langSubPart);
            }
        }
    }
}

/**
 * Restore all lang attributes to their original values
 */
function restoreLangAttribute() {
    const langHolderElements = document.querySelectorAll('[data-tts-original-lang]');
    for (let langHolderElement of langHolderElements) {
        langHolderElement.setAttribute('lang', langHolderElement.dataset.ttsOriginalLang);
        delete langHolderElement.dataset.ttsOriginalLang;
    }
}

/**
 * ReadWeb provider
 * @see http://leseweb.dk/
 *
 * @param {Object} config
 * @param {string} config.url - the base URL to load the Readweb script
 * @param {string} config.license - the license key
 * @param {boolean} [config.dev] - load Readweb in development mode (no cache)
 * @param {boolean} [config.showToolbar] - shows the Readweb toolbar (hidden by default)
 * @param {boolean} [config.loadingTimeoutMs=30000] - shows the Readweb toolbar (hidden by default)
 * @returns {Promise<ReadAloudClient>} resolves with the client
 */
export default function readwebProvider(config = {}) {
    if (!config.url) {
        return Promise.reject(new Error(`Missing service URL in the configuration 'config.url'`));
    }
    if (!config.license) {
        return Promise.reject(new Error(`Missing license key in the configuration 'config.license'`));
    }

    const scriptUrl = `${config.url}/${config.dev ? 'test-' : ''}${config.license}.js`;

    let ignoreSelector = config.ignoreSelector || '';
    let readStartHandler;
    let readEndHandler;
    let highlightedElementObserver;

    /**
     * Update the list of elements to ignore from the reading
     * @param {HTMLElement} container
     */
    function updateIgnoredElements(container) {
        if (ignoreSelector && container) {
            const elementsToIgnore = container.querySelectorAll(ignoreSelector);
            if (elementsToIgnore) {
                for (let elementToIgnore of elementsToIgnore) {
                    if (!elementToIgnore.classList.contains(ignoreClass)) {
                        elementToIgnore.classList.add(ignoreClass);
                    }
                }
            }
        }
    }

    /**
     * Creates an observer to follow the highlighted element and scroll the viewport to it
     * @param {HTMLElement} targetNode
     */
    function createHighlightedElementObserver(targetNode) {
        if (highlightedElementObserver) {
            destroyHighlightedElementObserver();
        }
        highlightedElementObserver = createScrollTargetObserver(targetNode, highlightClass);
    }

    /**
     * Destroys highlightedElementObserver
     */
    function destroyHighlightedElementObserver() {
        if (highlightedElementObserver) {
            highlightedElementObserver.disconnect();
            highlightedElementObserver = null;
        }
    }

    if (!config.showToolbar) {
        createToolBarContainer();
    }

    return loadReadWebScript(scriptUrl, config.loadingTimeoutMs).then(() => {
        if ('vFact_HTML5Player' in window) {
            window.vFact_HTML5Player.setEventHandler_OnChangePlaylistStatus(status => {
                reading = status !== 0;
                if (reading && typeof readStartHandler === 'function') {
                    readStartHandler();
                }
                if (!reading) {
                    if (typeof readEndHandler === 'function') {
                        readEndHandler();
                    }
                    restoreLangAttribute();
                }
            });
        }

        return {
            id: 'readweb',

            /**
             * Starts playing from the given element
             * @param {HTMLElement} element
             */
            play(element) {
                if (element instanceof HTMLElement && typeof window.vFact_playsection === 'function') {
                    //read web requires an id on element to read them
                    if (!element.id) {
                        element.id = generateElementId(element.nodeName);
                    }

                    //run it before every play in case of dynamic update of the nodes
                    updateIgnoredElements(element);
                    swapLangAttributes(element);

                    window.vFact_playsection(element.id);

                    if (this.autoscroll) {
                        createHighlightedElementObserver(element);
                    }
                }
            },

            /**
             * Plays the selected text
             */
            playSelection() {
                if (typeof window.vFact_doplay === 'function' && isTextSelected()) {
                    const ancestorElement = getSelectionAncestor();
                    updateIgnoredElements(ancestorElement);
                    swapLangAttributes(ancestorElement);

                    window.vFact_doplay();

                    if (this.autoscroll) {
                        createHighlightedElementObserver(ancestorElement);
                    }
                }
            },

            /**
             * Is Readweb currently reading
             * @returns {boolean} true when playing
             */
            isReading() {
                return reading;
            },

            /**
             * Calls the handler when reading starts
             * @param {function} handler
             */
            onReadStart(handler) {
                readStartHandler = handler;
            },

            /**
             * Calls the handler when reading ends
             * @param {function} handler
             */
            onReadEnd(handler) {
                readEndHandler = handler;
            },

            /**
             * Stop ongoing playing
             */
            stop() {
                if (typeof window.vFact_dostop === 'function') {
                    window.vFact_dostop();
                }

                if (highlightedElementObserver) {
                    destroyHighlightedElementObserver();
                }
            },

            /**
             * Prevent reading element matching a selector
             * @param {string} selector - a DOM selector
             */
            ignoreElements(selector) {
                if (ignoreSelector && selector) {
                    ignoreSelector = `${ignoreSelector},${selector}`;
                } else if (selector) {
                    ignoreSelector = selector;
                }
            },

            /**
             * Change the reading preferences
             * @param {Object} preferences
             * @param {string} preferences.speed - from the available speeds values
             * @param {string} preferences.pitch - from the available pitches values
             * @param {string} preferences.voice - from the available vocies values
             * @param {boolean} [preferences.autoscroll] - scroll viewport to element
             */
            setPreferences({ speed, pitch, voice, autoscroll } = {}) {
                if (typeof window.vfact_SetCustomParams === 'function') {
                    window.vfact_SetCustomParams(
                        typeof speedMap[speed] === 'number' ? speedMap[speed] : speedMap[speeds.normal],
                        typeof voiceMap[voice] === 'string' ? voiceMap[voice] : voiceMap[voices.female],
                        void 0, // this is an unused parameter by the library
                        typeof pitchMap[pitch] === 'number' ? pitchMap[pitch] : pitchMap[pitches.medium]
                    );
                }

                if (typeof autoscroll !== 'undefined') {
                    this.autoscroll = autoscroll;
                }
            },

            /**
             * Destroy provider
             */
            destroy() {
                this.stop();
            }
        };
    });
}
